/**
 * Structured editor’s-note blocks from the hub WP node — paragraphs plus
 * chapter headers and pullquotes for the black intro. Separators are parsed
 * but not rendered (chapter card borders replace them).
 */

import { resolveHubSlideLink } from "@story/data-centers-hub/config/resolveHubSlideLink";
import type { HubStateOutlineId } from "@story/data-centers-hub/config/stateOutlines";
import type { DeckSlideId } from "@story/data-centers-hub/config/slides";

export type EditorNoteBlock =
  | { type: "paragraph"; html: string; dropcap?: boolean }
  | {
      type: "heading";
      level: number;
      html: string;
      /** Chapter this header jumps to (chapter card group link). */
      slideId: DeckSlideId | null;
      /** State silhouette drawn in the chapter card. */
      state: HubStateOutlineId | null;
    }
  | { type: "pullquote"; html: string; citationHtml: string | null }
  | { type: "separator" };

const STATE_FROM_SLIDE: Partial<
  Record<DeckSlideId, HubStateOutlineId>
> = {
  virginia: "virginia",
  utah: "utah",
  arizona: "arizona",
};

const TEXT_SLIDE_ALIASES: ReadonlyArray<{
  pattern: RegExp;
  slideId: DeckSlideId;
}> = [
  { pattern: /\b(virginia|loudoun)\b/i, slideId: "virginia" },
  { pattern: /\butah\b/i, slideId: "utah" },
  { pattern: /\barizona\b/i, slideId: "arizona" },
];

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function firstHref(html: string): string | null {
  const match = html.match(/href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
  return match?.[1] ?? match?.[2] ?? match?.[3] ?? null;
}

/** Prefer an in-link target; fall back to the heading’s plain text. */
export function resolveNoteHeadingSlide(html: string): DeckSlideId | null {
  const href = firstHref(html);
  if (href) {
    const fromLink = resolveHubSlideLink(href);
    if (fromLink && fromLink !== "note") {
      return fromLink;
    }
  }
  const text = stripTags(html);
  for (const { pattern, slideId } of TEXT_SLIDE_ALIASES) {
    if (pattern.test(text)) {
      return slideId;
    }
  }
  return null;
}

function stateForSlide(slideId: DeckSlideId | null): HubStateOutlineId | null {
  if (!slideId) {
    return null;
  }
  return STATE_FROM_SLIDE[slideId] ?? null;
}

type HubNoteBlockNode = {
  readonly __typename?: string;
  readonly paragraphContents?: ReadonlyArray<{
    readonly html?: string | null;
  } | null> | null;
  readonly contents?:
    | ReadonlyArray<{ readonly html?: string | null } | null>
    | { readonly html?: string | null }
    | null;
  readonly citation?: { readonly html?: string | null } | null;
  readonly level?: number | null;
  readonly dropcap?: boolean | null;
};

function headingHtmlFromContents(
  contents: HubNoteBlockNode["contents"],
): string {
  if (!contents) {
    return "";
  }
  if (Array.isArray(contents)) {
    return contents
      .map((part) => part?.html?.trim() ?? "")
      .filter(Boolean)
      .join("");
  }
  if (typeof contents === "object" && "html" in contents) {
    return contents.html?.trim() ?? "";
  }
  return "";
}

export function editorNoteBlocksFromNode(blocks: unknown): EditorNoteBlock[] {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }
  const out: EditorNoteBlock[] = [];
  for (const raw of blocks) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const block = raw as HubNoteBlockNode;
    switch (block.__typename) {
      case "CoreParagraphBlockType": {
        const dropcap = block.dropcap === true;
        for (const part of block.paragraphContents ?? []) {
          const html = part?.html?.trim();
          if (html) {
            out.push(
              dropcap
                ? { type: "paragraph", html, dropcap: true }
                : { type: "paragraph", html },
            );
          }
        }
        break;
      }
      case "CoreHeadingBlockType": {
        const rawHtml = headingHtmlFromContents(block.contents);
        if (!rawHtml) {
          break;
        }
        const slideId = resolveNoteHeadingSlide(rawHtml);
        out.push({
          type: "heading",
          level: block.level && block.level >= 1 && block.level <= 6
            ? block.level
            : 2,
          // Chapter cards own the href; strip nested anchors at render.
          html: rawHtml,
          slideId,
          state: stateForSlide(slideId),
        });
        break;
      }
      case "CorePullquoteBlockType": {
        const html = headingHtmlFromContents(block.contents);
        if (!html) {
          break;
        }
        const citationHtml = block.citation?.html?.trim() || null;
        out.push({ type: "pullquote", html, citationHtml });
        break;
      }
      case "CoreSeparatorBlockType": {
        out.push({ type: "separator" });
        break;
      }
      default:
        break;
    }
  }
  return out;
}
